import { ContentTypeIds, RouteName, SUPPORTED_LOCALES } from '@/constants';
import type { ISitemapAlternate, ISlugItem, IUrlItem } from '@/interfaces';
import replacePath from './replacePath.utils';

const escapeXmlText = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeXmlAttribute = (value: string) => escapeXmlText(value).replace(/"/g, '&quot;');

export const generateSitemapXml = (items: IUrlItem[]) =>
  `<?xml version="1.0" encoding="UTF-8"?><?xml-stylesheet type="text/xsl" href="/main-sitemap.xsl"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">${items
    .map((item) => {
      const lastMod = item.lastMod ? `<lastmod>${item.lastMod}</lastmod>` : '';
      const alternates = (item.alternates ?? [])
        .map(
          (alternate) =>
            `<xhtml:link rel="alternate" hreflang="${escapeXmlAttribute(
              alternate.hrefLang,
            )}" href="${escapeXmlAttribute(alternate.href)}"/>`,
        )
        .join('');

      // Keep the core elements in sitemap protocol order, then emit extension elements.
      return `<url><loc>${escapeXmlText(
        item.location,
      )}</loc>${lastMod}<changefreq>daily</changefreq><priority>${escapeXmlText(
        item.priority,
      )}</priority>${alternates}</url>`;
    })
    .join('')}</urlset>`;

// One <url>…</url> entry. Non-greedy so each entry is matched separately.
const urlEntryRegex = /<url>[\s\S]*?<\/url>/g;
// A <loc> pointing at a *-tracking-api twin, with or without the trailing slash. Case-insensitive
// because slugs are not consistently lower case (e.g. "tracking-skroutz-AE").
// Non-global on purpose: .test() on a global regex advances lastIndex between calls.
const trackingApiLocRegex = /<loc>[^<]*-tracking-api\/?<\/loc>/i;
// An hreflang alternate on a kept entry can still point at a twin. The quote after the optional
// slash anchors the match to the end of the href, so mid-slug matches are left alone.
const trackingApiAlternateRegex = /<xhtml:link[^>]*-tracking-api\/?["'][^>]*>/gi;

/**
 * Drop the `*-tracking-api` entries from the NEO CMS tracking sitemap.
 *
 * `tracking-sitemap.xml` proxies NEO CMS verbatim, and NEO CMS emits a `<slug>-tracking-api`
 * twin alongside every carrier page that has an API page. Those twins are thin duplicates that
 * counted towards the GSC thin-content manual action (NEXUS-1502). They can only be removed at
 * source by NEO CMS, so until then this keeps them out of what we submit to Google.
 *
 * Their hreflang alternates go too, so no entry we keep points at a URL we just dropped.
 *
 * Anything that is not a urlset is returned untouched, so an upstream error payload can never
 * be turned into an empty sitemap — a sitemap with extra URLs beats no sitemap at all.
 * @example
 * excludeTrackingApiUrls('<urlset><url><loc>https://x/a-tracking-api/</loc></url></urlset>')
 * // => '<urlset></urlset>'
 */
export const excludeTrackingApiUrls = (xml: string) => {
  if (!xml?.includes('<urlset')) return xml;

  return xml
    .replace(urlEntryRegex, (entry) => (trackingApiLocRegex.test(entry) ? '' : entry))
    .replace(trackingApiAlternateRegex, '');
};

// Matches locale-prefixed slugs, e.g. "en/track-dhl" → ["en", "track-dhl"].
const localeSlugRegex = new RegExp(`^(${SUPPORTED_LOCALES.join('|')})/(.+)$`);

/**
 * Check if a slug item is valid or not, this is usually used for Preview mode since in Preview mode ths slug (required field) can be empty
 * @param item ISlugItem - { slug: '', type: '' }
 * @param type ContentTypeIds
 * @returns boolean
 * @example
 * checkInvalidISlugItem({ slug: 'news' }, 'tag') => true
 * checkInvalidISlugItem({ slug: '' }, 'article') => true
 * checkInvalidISlugItem({ slug: '', articleType: 'news' }, 'article') => true
 * checkInvalidISlugItem({ slug: 'article-1', articleType: 'news' }, 'article') => false
 */
const checkInvalidISlugItem = (item: ISlugItem, type: ContentTypeIds) => {
  switch (type) {
    case ContentTypeIds.landingPage:
      return !item.slug;
    default:
      return false;
  }
};

/**
 * Generate full slug from a slug item
 * @param item ISlugItem - { slug: '', type: '' }
 * @param type ContentTypeIds
 * @returns string
 * @example
 * generateFullSlugFromItem({ slug: '', type: 'news' }, 'tag') => '/news'
 * generateFullSlugFromItem({ slug: 'article-1', articleType: 'news' }, 'article') => '/news/article-1'
 */
const generateFullSlugFromItem = (item: ISlugItem, type: ContentTypeIds) => {
  switch (type) {
    case ContentTypeIds.landingPage:
      if (item.ampPageType) {
        return replacePath(RouteName.Article, [item.ampPageType, item.slug]);
      }

      return replacePath(RouteName.ArticleSummary, [item.slug]);
    default:
      return '';
  }
};

/**
 * Group locale-prefixed slugs by their template (slug without the locale prefix),
 * e.g. "en/track-dhl" & "vi/track-dhl" → template "track-dhl" with 2 locale variants.
 * Used to emit xhtml:link hreflang alternates in the sitemap, so hreflang always
 * reflects the currently published entries instead of a manually maintained field.
 */
const groupLocaleVariantsByTemplate = (items: ISlugItem[], type: ContentTypeIds) => {
  const variantsByTemplate = new Map<string, ISitemapAlternate[]>();

  for (const item of items) {
    const localeMatch = item.slug?.match(localeSlugRegex);
    if (!localeMatch) continue;

    const [, locale, template] = localeMatch;
    const variants = variantsByTemplate.get(template) ?? [];
    // One alternate per locale — ignore duplicate entries sharing the same slug
    if (!variants.some((variant) => variant.hrefLang === locale)) {
      variants.push({ hrefLang: locale, href: generateFullSlugFromItem(item, type) });
    }
    variantsByTemplate.set(template, variants);
  }

  return variantsByTemplate;
};

export const convertToSitemapItems = (
  items: ISlugItem[] | null,
  type: ContentTypeIds,
  baseUrl: string,
  excludedPaths?: Set<string>,
) => {
  if (!items) return [];

  // Excluded paths are dropped BEFORE grouping so they can never appear in a
  // sibling URL's hreflang alternates either.
  const validItems = items.filter(
    (item) => !checkInvalidISlugItem(item, type) && !excludedPaths?.has(generateFullSlugFromItem(item, type)),
  );
  const variantsByTemplate = groupLocaleVariantsByTemplate(validItems, type);

  return validItems.map<IUrlItem>((item) => {
    const urlItem: IUrlItem = {
      location: baseUrl + generateFullSlugFromItem(item, type),
      priority: '0.9',
    };

    // Contentful publishedAt is ISO 8601 — valid W3C Datetime for <lastmod> as-is
    if (item.sys?.publishedAt) {
      urlItem.lastMod = item.sys.publishedAt;
    }

    const localeMatch = item.slug?.match(localeSlugRegex);
    if (localeMatch) {
      const variants = variantsByTemplate.get(localeMatch[2]);

      // Only templates published in ≥2 locales form an hreflang cluster
      if (variants && variants.length >= 2) {
        const alternates = variants.map((variant) => ({
          hrefLang: variant.hrefLang,
          href: baseUrl + variant.href,
        }));

        const englishVariant = variants.find((variant) => variant.hrefLang === 'en');
        if (englishVariant) {
          alternates.push({ hrefLang: 'x-default', href: baseUrl + englishVariant.href });
        }

        urlItem.alternates = alternates;
      }
    }

    return urlItem;
  });
};
